import { IGeographyPointLocation } from "@dining/api-client";
import { Checkbox, Dropdown, Field, Input, Link, Option, Text } from "@fluentui/react-components";
import { createSelector } from "@reduxjs/toolkit";
import MDEditor, { ICommand } from "@uiw/react-md-editor";
import React, { useEffect, useRef, useState } from "react";
import { Controller, FieldPath, useFieldArray, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { validTimes } from "../../common/commonConstants";
import { handleInputCheck } from "../../common/invalidCharCheck";
import { fetchBuildings, setUpdatedCafeCountries } from "../../reducers/diningReducer";
import {
  buildingListSelector,
  cafeSelector,
  countriesSelector,
  regionListSelector,
  updateCafeModeSelector,
} from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import styles from "../../styles/updateCafe.module.css";
import { defaultUpdateCafeFormFields } from "./updateCafeHelper";

/* ---------- Types ---------- */
export type UpdateCafeFormFields = {
  cafeRegion: string;
  cafeCountry: string;
  cafeLocation: string;
  cafeName: string;
  mealPeriods: { name: string; startsOn: string; endsOn: string }[];
  currencyName: string;
  currencySymbol: string;
  cafeDisclaimer: string;
  location: IGeographyPointLocation;
};

/* ---------- Selectors ---------- */
const regionOptionsSelector = createSelector(regionListSelector, (regions) =>
  regions.map((r) => ({ key: r.id, text: r.name, countries: r.countries })),
);
const countriesOptionsSelector = createSelector(countriesSelector, (countries) =>
  countries.map((c) => ({ key: c.code ?? "", text: c.name })),
);
const buildingOptionsSelector = createSelector(buildingListSelector, (buildings) =>
  buildings.map((b) => ({
    key: b.id,
    text: isNaN(Number(b.name)) ? b.name : `Building ${b.name}`,
    location: b.location,
  })),
);

/* ---------- Component ---------- */
export const UpdateCafeForm = () => {
  const dispatch = useAppDispatch();
  const methods = useFormContext<UpdateCafeFormFields>();
  const { submitCount } = methods.formState;
  const focusedSubmitCount = useRef(0);

  const regions = useSelector(regionListSelector);
  const regionOptions = useSelector(regionOptionsSelector);
  const countriesOptions = useSelector(countriesOptionsSelector);
  const buildingOptions = useSelector(buildingOptionsSelector);
  const selectedCafe = useSelector(cafeSelector);
  const updateCafeMode = useSelector(updateCafeModeSelector);

  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [invalidCharError, setInvalidCharError] = useState("");
  const prevMode = useRef<string | null>(null);
  const disclaimerRef = useRef<HTMLDivElement>(null);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "mealPeriods",
  });

  // Conditional fields register later in RHF; use the form's visual order on each submission.
  useEffect(() => {
    if (submitCount === focusedSubmitCount.current) return;
    focusedSubmitCount.current = submitCount;
    if (submitCount === 0) return;

    const fieldOrder: FieldPath<UpdateCafeFormFields>[] = ["cafeRegion"];
    if (countriesOptions.length) fieldOrder.push("cafeCountry");
    fieldOrder.push("cafeLocation", "cafeName");
    if (isCustomLocation) {
      fieldOrder.push("location.coordinates.0", "location.coordinates.1");
    }
    fieldOrder.push("currencyName", "currencySymbol");
    fields.forEach((_, index) => {
      fieldOrder.push(`mealPeriods.${index}.name`, `mealPeriods.${index}.startsOn`, `mealPeriods.${index}.endsOn`);
    });
    fieldOrder.push("cafeDisclaimer");

    const firstInvalidField = fieldOrder.find((name) => methods.getFieldState(name).invalid);
    if (firstInvalidField) methods.setFocus(firstInvalidField);
  }, [submitCount, countriesOptions.length, isCustomLocation, fields, methods]);

  /* ---------- MDEditor Tab key fix ---------- */
  // Prevent MDEditor textarea from capturing Tab and instead
  // move focus to the panel's Save button
  useEffect(() => {
    const container = disclaimerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key !== "Tab" || target.tagName !== "TEXTAREA") return;
      e.preventDefault();
      e.stopPropagation();

      // Find the panel's Save or Cancel button
      const panelFooter = document.querySelector(".ms-Panel-footer, .ms-Panel-footerInner");
      if (panelFooter) {
        const targetButton = e.shiftKey
          ? (panelFooter.querySelector("button:last-of-type") as HTMLElement)
          : (panelFooter.querySelector("button") as HTMLElement);
        targetButton?.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown, true);
    return () => {
      container.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  /* ---------- MDEditor commands ---------- */
  const linkCommand: ICommand = {
    name: "insert-link",
    keyCommand: "link",
    buttonProps: {
      "aria-label": "Insert link",
      className: styles.insertLinkButton,
    },
    icon: <span style={{ filter: "brightness(0.3)", fontSize: "14px" }}>🔗</span>,
    execute: (_state, api) => api?.replaceSelection("[](url)"),
  };
  const editorCommands: ICommand[] = [linkCommand];
  const checkIfLocationisCustomized = () => {
    if (updateCafeMode === "Add") {
      setIsCustomLocation(false);
      return;
    } else {
      const selectedCafeBuilding = buildingOptions.find((item) => item.key === selectedCafe?.buildingId);
      const isCustomized = !(
        selectedCafeBuilding?.location?.coordinates[0] === selectedCafe?.location?.coordinates[0] &&
        selectedCafeBuilding?.location?.coordinates[1] === selectedCafe?.location?.coordinates[1]
      );
      setIsCustomLocation(isCustomized);
    }
  };

  useEffect(() => {
    checkIfLocationisCustomized();
  }, [buildingOptions]);
  /* ---------- Fetch dependent data ---------- */
  useEffect(() => {
    if (selectedCafe?.regionId) {
      const region = regions.find((r) => r.id === selectedCafe.regionId);
      dispatch(setUpdatedCafeCountries(region?.countries ?? []));
    }
    dispatch(fetchBuildings(selectedCafe?.countryCode));
  }, [selectedCafe, regions, dispatch]);

  /* ---------- Reset form on mode change ---------- */
  useEffect(() => {
    if (prevMode.current !== updateCafeMode) {
      if (updateCafeMode === "Edit") {
        methods.reset({
          cafeName: selectedCafe?.name ?? "",
          cafeRegion: selectedCafe?.regionId ?? "",
          cafeCountry: selectedCafe?.countryCode ?? "",
          cafeLocation: selectedCafe?.buildingId ?? "",
          location: selectedCafe?.location ?? {
            coordinates: ["", ""].map((coord) => (coord ? parseFloat(coord) : undefined)),
          },
          currencyName: selectedCafe?.currencyName ?? "",
          currencySymbol: selectedCafe?.currencySymbol ?? "",
          mealPeriods:
            selectedCafe?.mealPeriods?.map((mp) => ({
              ...mp,
              startsOn: mp.startsOn?.slice(11, 16),
              endsOn: mp.endsOn?.slice(11, 16),
            })) ?? [],
          cafeDisclaimer: selectedCafe?.disclaimer ?? "",
        });
      }
      if (updateCafeMode === "Add") {
        methods.reset(defaultUpdateCafeFormFields);

        // ✅ RESET LOCAL STATE
        setIsCustomLocation(false);
        setInvalidCharError("");
      }

      prevMode.current = updateCafeMode;
    }
  }, [updateCafeMode, selectedCafe]);

  return (
    <div className={styles.cafeForm}>
      {/* Region */}
      <Controller
        control={methods.control}
        name="cafeRegion"
        rules={{ required: "Required field." }}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          return (
            <Field
              label="Region"
              required
              validationState={hasError ? "error" : "none"}
              validationMessage={fieldState.error?.message}
            >
              <Dropdown
                ref={field.ref}
                onBlur={field.onBlur}
                selectedOptions={field.value ? [field.value] : []}
                aria-label={"Choose a region"}
                value={regionOptions.find((r) => r.key === field.value)?.text ?? ""}
                onOptionSelect={(_, data) => {
                  field.onChange(data.optionValue);
                  const region = regionOptions.find((r) => r.key === data.optionValue);
                  dispatch(setUpdatedCafeCountries(region?.countries ?? []));
                }}
              >
                {regionOptions.map((o) => (
                  <Option key={o.key} value={o.key} text={o.text ?? ""}>
                    <span>{o.text}</span>
                  </Option>
                ))}
              </Dropdown>
            </Field>
          );
        }}
      />
      {/* Country */}
      <Controller
        control={methods.control}
        name="cafeCountry"
        rules={{ required: "Required field." }}
        render={({ field, fieldState }) => (
          <Field
            label="Country"
            style={{
              opacity: !countriesOptions.length ? 0.5 : 1,
              pointerEvents: !countriesOptions.length ? "none" : "auto",
            }}
            required
            validationState={fieldState.error ? "error" : "none"}
            validationMessage={fieldState.error?.message}
          >
            <Dropdown
              ref={field.ref}
              onBlur={field.onBlur}
              selectedOptions={field.value ? [field.value] : []}
              value={countriesOptions.find((c) => c.key === field.value)?.text ?? ""}
              aria-label={"Choose a country"}
              disabled={!countriesOptions.length}
              onOptionSelect={(_, data) => {
                field.onChange(data.optionValue);
                dispatch(fetchBuildings(data.optionValue as string));
              }}
              className={`${fieldState.error}`}
            >
              {countriesOptions.map((o) => (
                <Option key={o.key} value={o.key} text={o.text ?? ""}>
                  {o.text}
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
      />
      {/* Location */}
      <Controller
        control={methods.control}
        name="cafeLocation"
        rules={{ required: "Required field." }}
        render={({ field, fieldState }) => (
          <Field
            label="Cafe location (i.e. building or campus)"
            required
            validationState={fieldState.error ? "error" : "none"}
            validationMessage={fieldState.error?.message}
          >
            <Dropdown
              ref={field.ref}
              onBlur={field.onBlur}
              selectedOptions={field.value ? [field.value] : []}
              value={buildingOptions.find((b) => b.key === field.value)?.text ?? ""}
              onOptionSelect={(_, data) => field.onChange(data.optionValue)}
              aria-label={"Choose a location, such as a building or campus"}
            >
              {buildingOptions.map((o) => (
                <Option key={o.key} value={o.key} text={o.text}>
                  <span>{o.text}</span>
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
      />
      {/* Cafe Name */}
      <Controller
        name="cafeName"
        control={methods.control}
        rules={{ required: "Required field." }}
        render={({ field, fieldState }) => (
          <Field
            label="Cafe name"
            required
            validationState={fieldState.error ? "error" : "none"}
            validationMessage={fieldState.error?.message}
          >
            <Input
              ref={field.ref}
              onBlur={field.onBlur}
              placeholder="Cafe name"
              data-testid={"cafeName"}
              aria-label={"Cafe name, required"}
              value={field.value ?? ""} // connects RHF value & onChange
              onChange={(e) => {
                field.onChange(e.target.value);
                setInvalidCharError(handleInputCheck(e.target.value));
              }}
            />
            {invalidCharError && <Text>{invalidCharError}</Text>}
          </Field>
        )}
      />
      {/* Custom Location */}
      <Checkbox
        label="Use custom location"
        checked={isCustomLocation}
        onChange={(_, d) => setIsCustomLocation(!!d.checked)}
      />
      {isCustomLocation && (
        <div className={styles.row}>
          {/* Latitude */}
          <Controller
            control={methods.control}
            name="location.coordinates.0"
            rules={{
              required: "Required field.",
              validate: (value) => !isNaN(value) || "Invalid value.",
            }}
            render={({ field, fieldState }) => (
              <Field
                label="Latitude"
                required
                validationState={fieldState.error ? "error" : "none"}
                validationMessage={fieldState.error?.message}
              >
                <Input
                  {...field}
                  value={field.value?.toString() || ""}
                  placeholder="Latitude"
                  aria-label="latitude number, required"
                  data-testid={"latitude"}
                />
              </Field>
            )}
          />

          {/* Longitude */}
          <Controller
            control={methods.control}
            name="location.coordinates.1"
            rules={{
              required: "Required field.",
              validate: (value) => !isNaN(value) || "Invalid value.",
            }}
            render={({ field, fieldState }) => (
              <Field
                label="Longitude"
                required
                validationState={fieldState.error ? "error" : "none"}
                validationMessage={fieldState.error?.message}
              >
                <Input
                  {...field}
                  value={field.value?.toString() || ""}
                  placeholder="Longitude"
                  aria-label="longitude number, required"
                  data-testid={"longitude"}
                />
              </Field>
            )}
          />
        </div>
      )}
      {/* Currency */}
      <div className={styles.row}>
        <Controller
          name="currencyName"
          control={methods.control}
          rules={{ required: "Required field." }}
          render={({ field, fieldState }) => (
            <Field
              label="Currency Name"
              required
              validationState={fieldState.error ? "error" : "none"}
              validationMessage={fieldState.error?.message}
            >
              <Input
                {...field}
                value={field.value?.toString() || ""}
                data-testid={"currencyName"}
                placeholder="USD or EUR"
              />
            </Field>
          )}
        />
        <Controller
          name="currencySymbol"
          control={methods.control}
          rules={{ required: "Required field." }}
          render={({ field, fieldState }) => (
            <Field
              label="Currency Symbol"
              required
              validationState={fieldState.error ? "error" : "none"}
              validationMessage={fieldState.error?.message}
            >
              <Input
                {...field}
                data-testid={"currencySymbol"}
                value={field.value?.toString() || ""}
                aria-label={"cafe currency symbol, required"}
                placeholder="$ or €"
              />
            </Field>
          )}
        />
      </div>
      {/* Meal Periods */}
      {fields.map((item, index) => (
        <fieldset key={item.id}>
          <Controller
            control={methods.control}
            name={`mealPeriods.${index}.name`}
            rules={{ required: "Required field." }}
            render={({ field, fieldState }) => (
              <Field
                label="Meal period name"
                required
                validationState={fieldState.error ? "error" : "none"}
                validationMessage={fieldState.error?.message}
              >
                <Input
                  {...field}
                  aria-label={"Meal period name, required"}
                  placeholder="Name of the period"
                  data-testid={`mealPeriods.${index}.name`}
                />
              </Field>
            )}
          />
          <Controller
            control={methods.control}
            name={`mealPeriods.${index}.startsOn`}
            rules={{ required: "Required field." }}
            render={({ field, fieldState }) => {
              const hasError = !!fieldState.error;
              return (
                <Field
                  label="Cafe opens"
                  required
                  validationState={hasError ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                >
                  <Dropdown
                    ref={field.ref}
                    onBlur={field.onBlur}
                    aria-label="Cafe opens"
                    placeholder="--:--"
                    selectedOptions={field.value ? [field.value] : []}
                    onOptionSelect={(_, d) => field.onChange(d.optionValue)}
                    value={field.value || "--:--"}
                    className={styles.dropdownWidth}
                  >
                    {validTimes.map((t) => (
                      <Option key={t} value={t}>
                        {t}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              );
            }}
          />
          <Controller
            control={methods.control}
            name={`mealPeriods.${index}.endsOn`}
            rules={{ required: "Required field." }}
            render={({ field, fieldState }) => (
              <Field
                label="Cafe closes"
                required
                validationState={fieldState.error ? "error" : "none"}
                validationMessage={fieldState.error?.message}
              >
                <Dropdown
                  ref={field.ref}
                  onBlur={field.onBlur}
                  aria-label={"Cafe closes"}
                  placeholder="--:--"
                  value={field.value || "--:--"}
                  selectedOptions={field.value ? [field.value] : []}
                  onOptionSelect={(_, d) => field.onChange(d.optionValue)}
                  className={styles.dropdownWidth}
                >
                  {validTimes.map((t) => (
                    <Option key={t} value={t}>
                      {t}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
          />
          <Link onClick={() => remove(index)}>Remove</Link>
        </fieldset>
      ))}
      <Link onClick={() => append({ name: "", startsOn: "", endsOn: "" })}>Add meal period</Link>
      {/* Disclaimer */}
      <Controller
        control={methods.control}
        name="cafeDisclaimer"
        rules={{ required: "Required field." }}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          return (
            <Field
              label="Disclaimer"
              required
              validationState={hasError ? "error" : "none"}
              validationMessage={fieldState.error?.message}
            >
              <div ref={disclaimerRef}>
                <MDEditor
                  ref={(editor) => field.ref(editor?.textarea ?? null)}
                  preview="edit"
                  aria-label={"Cafe disclaimer, required"}
                  value={field.value}
                  onChange={(v) => field.onChange(v?.slice(0, 250) ?? "")}
                  data-color-mode="light"
                  textareaProps={{
                    name: field.name,
                    onBlur: field.onBlur,
                    "aria-label": "Cafe disclaimer, required",
                    "aria-invalid": hasError,
                    placeholder:
                      "Menu items, prices, availability, and opening hours may change. Please check in-store for the most up-to-date information.",
                  }}
                  commands={editorCommands}
                  className={hasError ? styles.inputError : ""} // Add conditional class for error state
                />
              </div>
            </Field>
          );
        }}
      />
    </div>
  );
};
